import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  LuCloudUpload,
  LuExternalLink,
  LuFile,
  LuFileImage,
  LuFileSpreadsheet,
  LuFileText,
  LuFolderOpen,
  LuLoaderCircle,
  LuTrash2,
  LuUpload,
} from 'react-icons/lu';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import './FileUploadModal.module.css';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/ui/Modal';
import { GLASS_CHIP, ICON_BUTTON } from '../../components/ui/glass';
import { Field, SelectInput } from '../../components/form/FormControls';
import { db, storage } from '../../config/firebaseConfig';
import { useTranslation } from '../../i18n/LanguageContext';

// Dosya türleri (Storage yolunda ve kayıtta bu değerler kullanılır)
const FILE_TYPES = ['ilkBakanlikOnayi', 'ilkEtikKurulOnayi', 'etikKurulSunumu', 'etikKurulKarari', 'etikKurulOnayi', 'titck', 'diger'];

const fileVisual = (fileName) => {
  const extension = String(fileName || '').split('.').pop().toLowerCase();
  if (extension === 'pdf') return { icon: LuFileText, tone: 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300' };
  if (['doc', 'docx'].includes(extension)) return { icon: LuFileText, tone: 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-text-blue-300' };
  if (['xls', 'xlsx', 'csv'].includes(extension)) return { icon: LuFileSpreadsheet, tone: 'tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300' };
  if (['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(extension)) return { icon: LuFileImage, tone: 'tw-bg-amber-500/10 tw-text-amber-600 dark:tw-text-amber-300' };
  return { icon: LuFile, tone: 'tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300' };
};

/**
 * "Dosyalar" of a project: pick a type, drop or choose a file; it goes to
 * Storage (projects/{id}/{type}/{name}) and onto the project's `files` list.
 * Files open in a new tab and can be deleted (Storage and list).
 */
const FileUploadModal = ({ onClose, project }) => {
  const { t, language, locale } = useTranslation();
  const [fileType, setFileType] = useState('');
  const [typeError, setTypeError] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [confirmingUrl, setConfirmingUrl] = useState(null);
  const [deletingUrl, setDeletingUrl] = useState(null);
  const fileInputRef = useRef(null);

  const typeLabel = (type) => (FILE_TYPES.includes(type) ? t(`projectManagement.files.types.${type}`) : type);
  const formatDate = (dateString) => (dateString
    ? new Intl.DateTimeFormat(locale, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(new Date(dateString))
    : '');

  const fetchUploadedFiles = async () => {
    try {
      const projectDoc = await getDoc(doc(db, 'Projects', project.id));
      const projectData = projectDoc.data();
      if (projectData.files) setUploadedFiles(projectData.files);
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUploadedFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const handleFileUpload = async (file) => {
    if (!fileType) {
      setTypeError(true);
      toast.warning(t('projectManagement.files.chooseType'));
      return;
    }
    setIsUploading(true);
    try {
      const storagePath = `projects/${project.id}/${fileType}/${file.name}`;
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      const projectRef = doc(db, 'Projects', project.id);
      const newFile = {
        type: fileType,
        name: file.name,
        url: downloadURL,
        uploadedAt: new Date().toISOString(),
        storagePath,
      };
      const projectData = (await getDoc(projectRef)).data();
      await updateDoc(projectRef, { files: projectData.files ? [...projectData.files, newFile] : [newFile] });
      await fetchUploadedFiles();
      toast.success(t('projectManagement.files.uploaded', { name: file.name }));
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(t('projectManagement.files.uploadError'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async (fileToDelete) => {
    setDeletingUrl(fileToDelete.url);
    try {
      const storagePath = fileToDelete.storagePath || `projects/${project.id}/${fileToDelete.type}/${fileToDelete.name}`;
      await deleteObject(ref(storage, storagePath));

      const projectRef = doc(db, 'Projects', project.id);
      const projectData = (await getDoc(projectRef)).data();
      if (projectData.files) {
        await updateDoc(projectRef, { files: projectData.files.filter((file) => file.url !== fileToDelete.url) });
      }
      await fetchUploadedFiles();
      toast.success(t('projectManagement.files.deleted'));
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(t('projectManagement.files.deleteError', { message: error.message }));
    } finally {
      setDeletingUrl(null);
      setConfirmingUrl(null);
    }
  };

  const openPicker = () => {
    if (isUploading) return;
    if (!fileType) {
      setTypeError(true);
      toast.warning(t('projectManagement.files.chooseType'));
      return;
    }
    fileInputRef.current?.click();
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(event.type === 'dragenter' || event.type === 'dragover');
  };

  const handleDrop = (event) => {
    handleDrag(event);
    if (!isUploading && event.dataTransfer.files.length > 0) handleFileUpload(event.dataTransfer.files[0]);
  };

  const renderFiles = () => {
    if (loading) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
          <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
          {t('projectManagement.files.loading')}
        </div>
      );
    }
    if (uploadedFiles.length === 0) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-dashed tw-border-slate-200 tw-px-4 tw-py-10 tw-text-center dark:tw-border-white/10">
          <LuFolderOpen aria-hidden="true" className="tw-h-7 tw-w-7 tw-text-slate-300 dark:tw-text-slate-600" />
          <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{t('projectManagement.files.none')}</p>
        </div>
      );
    }
    return (
      <ul className="tw-space-y-2">
        {uploadedFiles.map((file, index) => {
          const { icon: Icon, tone } = fileVisual(file.name);
          const confirming = confirmingUrl === file.url;
          const deleting = deletingUrl === file.url;
          return (
            <li key={`${file.url}-${index}`} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-p-2.5 ${GLASS_CHIP}`}>
              <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tone}`}>
                <Icon aria-hidden="true" className="tw-h-5 tw-w-5" />
              </span>
              <div className="tw-min-w-0 tw-flex-1">
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={file.name}
                  className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-800 tw-transition-colors hover:tw-text-teal-700 dark:tw-text-slate-100 dark:hover:tw-text-teal-300"
                >
                  {file.name}
                </a>
                <p className="tw-mt-1 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  <span className="tw-max-w-full tw-truncate tw-rounded-full tw-bg-indigo-500/10 tw-px-2 tw-py-0.5 tw-font-semibold tw-text-indigo-700 dark:tw-text-indigo-300">
                    {typeLabel(file.type)}
                  </span>
                  {file.uploadedAt && <span className="tw-tabular-nums">{formatDate(file.uploadedAt)}</span>}
                </p>
              </div>
              {confirming ? (
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                  <span className="tw-hidden tw-text-xs tw-font-semibold tw-text-rose-600 sm:tw-inline dark:tw-text-rose-400">{t('projectManagement.files.confirmDelete')}</span>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmingUrl(null)} disabled={deleting}>{t('projectManagement.files.keep')}</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDeleteFile(file)} disabled={deleting}>
                    {deleting
                      ? <LuLoaderCircle aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
                      : <LuTrash2 aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />}
                    {t('projectManagement.files.delete')}
                  </Button>
                </div>
              ) : (
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1.5">
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={t('projectManagement.files.open')}
                    aria-label={t('projectManagement.files.open')}
                    className={`${ICON_BUTTON} tw-text-slate-600 hover:tw-text-slate-900 dark:tw-text-slate-300 dark:hover:tw-text-white`}
                  >
                    <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </a>
                  <IconButton
                    label={t('projectManagement.files.delete')}
                    tone="alert"
                    onClick={() => setConfirmingUrl(file.url)}
                    disabled={isUploading || Boolean(deletingUrl)}
                    className="disabled:tw-opacity-50"
                  >
                    <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </IconButton>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Modal
      isOpen
      onClose={() => !isUploading && onClose()}
      size="form"
      title={t('projectManagement.files.title')}
      subtitle={project.titleShort || project.customer}
      icon={(
        <GlassIcon gradient="tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuFolderOpen aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      footer={<Button variant="ghost" onClick={onClose} disabled={isUploading}>{t('common.close')}</Button>}
    >
      <div className="tw-grid tw-gap-5 md:tw-grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
        {/* Upload */}
        <div className="tw-space-y-3">
          <Field
            label={t('projectManagement.files.type')}
            htmlFor="project-file-type"
            required
            error={typeError ? t('projectManagement.files.chooseType') : undefined}
          >
            <SelectInput
              id="project-file-type"
              value={fileType}
              onChange={(event) => {
                setFileType(event.target.value);
                setTypeError(false);
              }}
              disabled={isUploading}
              placeholder={t('dataTable.form.choose')}
              error={typeError ? 'error' : undefined}
              options={FILE_TYPES.map((type) => ({ value: type, label: typeLabel(type) }))}
            />
          </Field>

          <input
            ref={fileInputRef}
            type="file"
            onChange={(event) => {
              const file = event.target.files[0];
              // Cleared so the same file can be chosen again
              event.target.value = '';
              if (file) handleFileUpload(file);
            }}
            className="tw-hidden"
            disabled={isUploading}
          />

          <div
            role="button"
            tabIndex={0}
            aria-disabled={isUploading || undefined}
            onClick={openPicker}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openPicker();
              }
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2 tw-rounded-2xl tw-border-2 tw-border-dashed tw-px-4 tw-py-7 tw-text-center tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
              dragActive
                ? 'tw-border-teal-500 tw-bg-teal-500/10'
                : 'tw-border-slate-300 tw-bg-slate-50/60 hover:tw-border-teal-400 hover:tw-bg-teal-500/[0.04] dark:tw-border-white/15 dark:tw-bg-white/[0.02] dark:hover:tw-border-teal-400/60'
            }`}
          >
            <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="md">
              {isUploading
                ? <LuLoaderCircle aria-hidden="true" className="tw-h-6 tw-w-6 tw-animate-spin" />
                : <LuCloudUpload aria-hidden="true" className="tw-h-6 tw-w-6" />}
            </GlassIcon>
            {isUploading ? (
              <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{t('projectManagement.files.uploading')}</p>
            ) : (
              <>
                <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{t('projectManagement.files.drop')}</p>
                <p className="tw-text-xs tw-text-slate-400">{t('projectManagement.files.or')}</p>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={isUploading || !fileType}
                  onClick={(event) => {
                    event.stopPropagation();
                    openPicker();
                  }}
                >
                  <LuUpload aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                  {t('projectManagement.files.browse')}
                </Button>
                {!fileType && <p className="tw-text-[11px] tw-text-slate-400 dark:tw-text-slate-500">{t('projectManagement.files.typeFirst')}</p>}
              </>
            )}
          </div>
        </div>

        {/* Uploaded files */}
        <div className="tw-min-w-0 tw-space-y-3">
          <p className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
            <LuFolderOpen aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {t('projectManagement.files.list', { count: uploadedFiles.length })}
          </p>
          <div className="tw-pr-1 md:tw-max-h-[min(24rem,52vh)] md:tw-overflow-y-auto glass-scrollbar">{renderFiles()}</div>
        </div>
      </div>
    </Modal>
  );
};

FileUploadModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  project: PropTypes.object.isRequired,
};

export default FileUploadModal;
